import { SelfContainedCommand } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { entityService } from '../../../services/entityService';
import { generateCitationKey } from '../types/reference';

/**
 * AI command to search Library references
 * 
 * Example AI usage:
 *   AI: "Let me search for papers on autism interventions"
 *   Tool: library_search_references(query="autism interventions")
 *   Result: [...list of references with titles, authors, abstracts...]
 */
export const librarySearchReferencesCommand: SelfContainedCommand = {
  id: 'library_search_references',
  description: 'Search the Library reference collection. Returns references with full metadata including titles, authors, years, abstracts, and tags. Use this to find papers to cite.',
  
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query. Can be keywords, author names, topics, etc.',
        required: true
      },
      {
        name: 'filters',
        type: 'object',
        description: 'Optional filters for search. Can include: tags (array), yearMin (number), yearMax (number), authors (array)',
        required: false
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results (default 20)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { query: 'autism interventions', limit: 30 },
        expectedOutput: {
          status: 'success',
          message: 'Found 12 references matching "autism interventions"',
          format: 'json',
          data: { count: 12, references: [] }
        },
        description: 'Search for papers on autism interventions'
      },
      {
        input: { query: 'machine learning', filters: { yearMin: 2020, tags: ['deep learning'] } },
        expectedOutput: {
          status: 'success',
          message: 'Found 8 references matching "machine learning"',
          format: 'json',
          data: { count: 8, references: [] }
        },
        description: 'Search with filters for recent papers'
      }
    ],
    bad: []
  },
  
  handler: async (params, context): Promise<StandardToolResult> => {
    try {
      const { query, filters = {}, limit = 20 } = params;
      const { spaceId } = context;
      
      // Get all references
      const allReferences = await entityService.queryEntities({
        entityType: 'Reference',
        spaceId
      });
      
      // Filter by query
      let results = allReferences.filter(ref => {
        const searchableText = [
          ref.name || '',
          ref.skeleton?.title || '',
          ref.skeleton?.abstract || '',
          ...(ref.skeleton?.authors || []),
          ...(ref.skeleton?.tags || [])
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(query.toLowerCase())) {
          return false;
        }
        
        // Apply tag filters
        if (filters.tags?.length) {
          const refTags = ref.skeleton?.tags || [];
          if (!filters.tags.some((tag: string) => refTags.includes(tag))) {
            return false;
          }
        }
        
        // Apply year filters
        if (filters.yearMin && ref.skeleton?.year < filters.yearMin) {
          return false;
        }
        
        if (filters.yearMax && ref.skeleton?.year > filters.yearMax) {
          return false;
        }
        
        // Apply author filters
        if (filters.authors?.length) {
          const refAuthors = (ref.skeleton?.authors || []).join(' ').toLowerCase();
          if (!filters.authors.some((author: string) => 
            refAuthors.includes(author.toLowerCase())
          )) {
            return false;
          }
        }
        
        return true;
      });
      
      // Sort by year (most recent first)
      results.sort((a, b) => (b.skeleton?.year || 0) - (a.skeleton?.year || 0));
      
      // Limit results
      results = results.slice(0, limit);
      
      // Format for AI
      const formatted = results.map(ref => ({
        id: ref._id,
        title: ref.skeleton?.title || ref.name,
        authors: ref.skeleton?.authors || [],
        year: ref.skeleton?.year,
        journal: ref.skeleton?.journal,
        abstract: ref.skeleton?.abstract,
        tags: ref.skeleton?.tags || [],
        citationKey: ref.skeleton?.citationKey || generateCitationKey(ref.skeleton?.authors || [], ref.skeleton?.year || 0),
        hasPDF: !!ref.skeleton?.primaryPdfId,
        doi: ref.skeleton?.doi
      }));
      
      return createSuccessResult(
        `Found ${formatted.length} references matching "${query}"`,
        {
          query,
          count: formatted.length,
          references: formatted
        }
      );
    } catch (error) {
      return createErrorResult(
        'Failed to search references',
        error instanceof Error ? error.message : 'Unknown error',
        'execution'
      );
    }
  }
};

